import { uniq } from 'lodash-es';
import { CustomerMT, PaginatedCustomerListQueryFilters } from '@/api/apis/customer';
import { api } from '@/api';
import { getTXCentersByIds, txCenterQueries } from '@/queries/tx-centers';
import { PaginatedBody } from '@/api/types';
import { queryClient } from '@/queries';

export const getCustomerListWithTXCenter = async (
  pagination: PaginatedCustomerListQueryFilters,
): Promise<PaginatedBody<CustomerMT>> => {
  const data = await api.customer.getCustomerList(pagination);

  if (data) {
    const txCenterIds = uniq(data.items.flatMap((c) => c.customerInTxCenters.map((tx) => tx.txCenterId) || []));
    const txCenters = await getTXCentersByIds(txCenterIds);

    if (txCenters) {
      return {
        ...data,
        items: data.items.map((c) => ({
          ...c,
          txCentersList: txCenters.items.filter((e) => c.customerInTxCenters.some((tx) => tx.txCenterId === e.id)),
        })),
      };
    }
  }

  return data;
};

export const getCustomerByIdWithTXCenter = async ({
  userId,
  customerId = '',
}: {
  userId?: string;
  customerId?: string;
}): Promise<CustomerMT> => {
  const data = userId ? await api.customer.getCustomerByUserId(userId) : await api.customer.getCustomerById(customerId);

  if (data) {
    const customerWithTxCenters = await Promise.all(
      data.customerInTxCenters.map(async (i) => {
        const txCenterData = await queryClient.ensureQueryData(txCenterQueries.item(i.txCenterId));

        return {
          ...i,
          txCenter: txCenterData,
        };
      }),
    );

    return {
      ...data,
      customerInTxCenters: customerWithTxCenters,
    };
  }

  return data;
};

export const getCustomerItemUpdater = (payload: Partial<CustomerMT>) => (data?: CustomerMT) => {
  if (!data) return undefined;

  return { ...data, ...payload };
};

export const getCustomerListItemUpdater =
  (customerId: string, payload: Partial<CustomerMT>) =>
  (data?: PaginatedBody<CustomerMT>): PaginatedBody<CustomerMT> | undefined => {
    if (data) {
      const items = data.items.map((c) => (c.id === customerId ? { ...c, ...payload } : c));
      return { ...data, items };
    }
    return undefined;
  };
